import { getAccessToken, getCockpitUser, isAppControl } from "@newsenec/auth-provider";
import { buildFetchErrorObj, sendErrorObjToBackendIfNew } from "@newsenec/logging";
import { mf, mfShorty } from "../constants";
import { changeLoginState } from "./flow";
import {
    currentLanguage,
    currentMarket,
    fallbackMarket,
    switchLanguageByMarket,
    switchMarket,
} from "@newsenec/language-provider";
import { removeLoadingView, showLoadingView } from "../fpp-app/loading/loadingView";
import { ErrorSerial } from "../ErrorSerial";

interface UserTermsOfUseResponse {
    signedLatest: boolean;
    url: string;
    version: string;
}

export const fetchTerms = async () => {
    const token = await getAccessToken();
    const url = new URL(`${location.origin}/api/fpp/terms/${currentLanguage()}`);
    const method = "GET";

    const response = await fetch(url, {
        method,
        headers: { Authorization: "Bearer " + token },
    });

    if (!response.ok) return Promise.reject(await buildFetchErrorObj(mfShorty, method, response, ErrorSerial.TERMS_RETRIEVAL));

    return response.json() as Promise<UserTermsOfUseResponse>;
};

export const termsApproval = async () => {
    showLoadingView();

    try {
        const terms = isAppControl() ? { signedLatest: true } : await fetchTerms();
        await setMarketAndLanguage();

        if (terms.signedLatest) {
            changeLoginState("termsApproved");
        } else {
            changeLoginState("termsNotApproved");
        }
    } catch (error) {
        // start terms approval to use its error view
        changeLoginState("termsNotApproved");
        sendErrorObjToBackendIfNew(mf, mfShorty, error);
    }

    removeLoadingView();
};

export const setMarketAndLanguage = async () => {
    /**
     * internationalization means that we differentiate between market and language
     * and can also switch the language within a market
     * e.g. market DE -> language de
     *      market DE -> language en
     *      market DE -> language it
     *      ...
     *
     * if internationalization is disabled market and language are seen as the same,
     * which means that there is only one set language for a market
     * e.g. ONLY: market DE -> language de
     */
    const market = isAppControl() ? fallbackMarket : await getMarket();
    const storedMarket = currentMarket();
    if (storedMarket !== market) {
        await switchMarket(market, false);
        await switchLanguageByMarket(false);
    }
};

export const getMarket = async (): Promise<string> => {
    return getCockpitUser()
        .then((user) => user.crmUser.market ?? currentMarket())
        .catch(() => currentMarket());
};
